import { ReactNode, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronUp, FiMessageSquare, FiFileText } from "react-icons/fi";
import { DueDiligenceTier } from "../../../Common/Types-DTOs/DueDiligence";

const TIERS: {
  value: DueDiligenceTier;
  fallbackPrice: string;
  hasBadge?: boolean;
  chatbotBadge?: boolean;
}[] = [
  {
    value: "basic",
    fallbackPrice: "29 €",
  },
  {
    value: "professional",
    fallbackPrice: "499 €",
    hasBadge: true,
    chatbotBadge: true,
  },
  {
    value: "premium",
    fallbackPrice: "1.990 €",
    chatbotBadge: true,
  },
];

const SelectorGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, 1fr);
  gap: 8px;
`;

const TierOption = styled.button<{ $selected: boolean }>`
  position: relative;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$selected ? "#67D9D4" : "#e5e7eb")};
  background-color: ${(p) => (p.$selected ? "#F0FFFE" : "#fafafa")};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$selected ? "#67D9D4" : "#9CA3AF")};
  }
`;

const TierOptionSubtitle = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9ca3af;
`;

const TierOptionTitle = styled.span<{ $selected: boolean }>`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => (p.$selected ? "#0D9488" : "#111827")};
  line-height: 1.3;
`;

const TierOptionPrice = styled.span<{ $selected: boolean }>`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(p) => (p.$selected ? "#0D9488" : "#6b7280")};
  margin-top: 4px;
`;

const TierOptionNote = styled.span`
  font-size: 0.7rem;
  color: #6b7280;
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -9px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #001d30;
  color: white;
  font-size: 0.6rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
`;

const DeliveryNote = styled.p`
  margin: 10px 0 0;
  font-size: 0.75rem;
  color: #6b7280;
`;

const DetailsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.12s;

  &:hover {
    color: #374151;
  }
`;

const DetailsPanel = styled.div`
  margin-top: 10px;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #6b7280;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const ChatbotPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  padding: 3px 8px;
  border-radius: 999px;
  background-color: #a8fbca;
  color: #001d30;
  font-size: 0.7rem;
  font-weight: 700;
`;

const FeatureList = styled.ul`
  margin: 0;
  padding: 0 0 0 14px;
  font-size: 0.8rem;
  color: #374151;
  line-height: 1.75;
`;

const DocsSection = styled.div`
  border-top: 1px solid #e5e7eb;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocsSubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.67rem;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const DocsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #9ca3af;
  font-size: 0.62rem;
  font-weight: 700;
  border-radius: 999px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
`;

const DocsList = styled.ul`
  margin: 0;
  padding: 0 0 0 14px;
  font-size: 0.77rem;
  color: #374151;
  line-height: 1.65;
`;

interface TierSelectorProps {
  selectedTier: DueDiligenceTier;
  onTierChange: (tier: DueDiligenceTier) => void;
  pricing?: { basic: number | null; professional: number | null; premium: number | null } | null;
  /** Restricts which tiers are offered (e.g. only tiers above the one already paid). Defaults to all 3. */
  tiers?: DueDiligenceTier[];
  /** Shows each tier priced in credits instead of euros. */
  creditCosts?: Record<DueDiligenceTier, number> | null;
  /** Tiers shown but not selectable, each with why: it shows where the price would. */
  unavailableTiers?: Partial<Record<DueDiligenceTier, string>>;
  /** A second figure under each tier's price, e.g. the price in the other way of paying. */
  notes?: Partial<Record<DueDiligenceTier, ReactNode>>;
  /** Replaces a tier's price as it would show, e.g. with what an upgrade to it costs. */
  priceLabels?: Partial<Record<DueDiligenceTier, string>>;
}

export const TierSelector: React.FC<TierSelectorProps> = ({
  selectedTier,
  onTierChange,
  pricing,
  tiers,
  creditCosts,
  unavailableTiers,
  notes,
  priceLabels,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const visibleTiers = tiers ? TIERS.filter((tierItem) => tiers.includes(tierItem.value)) : TIERS;
  const activeTier = visibleTiers.find((tier) => tier.value === selectedTier) ?? visibleTiers[0];

  const getFeatures = (value: DueDiligenceTier) =>
    t(`dueDiligence.tierSelector.tiers.${value}.features`, { returnObjects: true }) as string[];
  const getDocumentationIncluded = (value: DueDiligenceTier) =>
    t(`dueDiligence.tierSelector.tiers.${value}.documentationIncluded`, { returnObjects: true }) as string[];
  const getDocumentationRequired = (value: DueDiligenceTier) =>
    t(`dueDiligence.tierSelector.tiers.${value}.documentationRequired`, { returnObjects: true }) as string[];

  return (
    <div>
      <SelectorGrid $columns={visibleTiers.length}>
        {visibleTiers.map((tier) => {
          const selected = selectedTier === tier.value;
          const unavailableReason = unavailableTiers?.[tier.value];
          const dynamicPrice = pricing?.[tier.value];
          const priceLabel = unavailableReason
            ? unavailableReason
            : priceLabels?.[tier.value]
              ? priceLabels[tier.value]
              : creditCosts
              ? t("dueDiligence.tierSelector.creditsLabel", { count: creditCosts[tier.value] })
              : dynamicPrice != null
                ? t("dueDiligence.tierSelector.priceLabel", { price: `${dynamicPrice} €` })
                : t("dueDiligence.tierSelector.priceLabel", { price: tier.fallbackPrice });

          return (
            <TierOption
              key={tier.value}
              type="button"
              $selected={selected}
              disabled={Boolean(unavailableReason)}
              onClick={() => onTierChange(tier.value)}
            >
              {tier.hasBadge && (
                <PopularBadge>{t(`dueDiligence.tierSelector.tiers.${tier.value}.badge`)}</PopularBadge>
              )}
              <TierOptionSubtitle>{t(`dueDiligence.tierSelector.tiers.${tier.value}.subtitle`)}</TierOptionSubtitle>
              <TierOptionTitle $selected={selected}>
                {t(`dueDiligence.tierSelector.tiers.${tier.value}.title`)}
              </TierOptionTitle>
              <TierOptionPrice $selected={selected}>{priceLabel}</TierOptionPrice>
              {notes?.[tier.value] && <TierOptionNote>{notes[tier.value]}</TierOptionNote>}
            </TierOption>
          );
        })}
      </SelectorGrid>

      {(selectedTier === 'professional' || selectedTier === 'premium') && (
        <DeliveryNote>
          {t("dueDiligence.tierSelector.deliveryNote", {
            deliveryTime: t(`dueDiligence.tierSelector.tiers.${activeTier.value}.deliveryTime`),
          })}
        </DeliveryNote>
      )}

      <DetailsToggle type="button" onClick={() => setShowDetails((v) => !v)}>
        <FiFileText size={13} />
        {showDetails
          ? t("dueDiligence.tierSelector.detailsToggle.hide")
          : t("dueDiligence.tierSelector.detailsToggle.show")}
        {showDetails ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
      </DetailsToggle>

      {showDetails && (
        <DetailsPanel>
          <DetailRow>
            <DetailLabel>{t("dueDiligence.tierSelector.audienceLabel")}</DetailLabel>
            {t(`dueDiligence.tierSelector.tiers.${activeTier.value}.audience`)}
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t("dueDiligence.tierSelector.deliveryTimeLabel")}</DetailLabel>
            {t(`dueDiligence.tierSelector.tiers.${activeTier.value}.deliveryTime`)}
          </DetailRow>
          {activeTier.chatbotBadge && (
            <ChatbotPill>
              <FiMessageSquare size={11} />
              {t("dueDiligence.tierSelector.chatbotIncluded")}
            </ChatbotPill>
          )}
          <FeatureList>
            {getFeatures(activeTier.value).map((f) => (
              <li key={f}>{f}</li>
            ))}
          </FeatureList>
          <DocsSection>
            <DocsSubHeader>
              {t("dueDiligence.tierSelector.documentationIncludedHeader")}
              <DocsBadge>{getDocumentationIncluded(activeTier.value).length}</DocsBadge>
            </DocsSubHeader>
            <DocsList>
              {getDocumentationIncluded(activeTier.value).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </DocsList>
            <DocsSubHeader>
              {t("dueDiligence.tierSelector.documentationRequiredHeader")}
              <DocsBadge>{getDocumentationRequired(activeTier.value).length}</DocsBadge>
            </DocsSubHeader>
            <DocsList>
              {getDocumentationRequired(activeTier.value).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </DocsList>
          </DocsSection>
        </DetailsPanel>
      )}
    </div>
  );
};
